import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "@/stores/authStore";
import { useChatStore } from "@/stores/chatStore";
import { useMapStore } from "@/stores/mapStore";
import { useMarketStore } from "@/stores/marketStore";
import { renameConversation } from "@/services/api";
import type { Conversation } from "@/types";

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
  onLoadConversation: (conversationId: string) => void;
  onDeleteConversation: (conversationId: string) => void;
  onOpenSettings?: () => void;
}

// ── Icons ──

function PanelIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2.5" />
      <line x1="9" y1="3" x2="9" y2="21" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
      <circle cx="10.5" cy="10.5" r="6.5" />
      <line x1="19" y1="19" x2="15.5" y2="15.5" />
    </svg>
  );
}

function ChatBubbleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function GearIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

function SunIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="5" />
      <line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" />
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
      <line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" />
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

function DotsThreeIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
      <circle cx="5" cy="12" r="2" />
      <circle cx="12" cy="12" r="2" />
      <circle cx="19" cy="12" r="2" />
    </svg>
  );
}


// ── NavRow — unified icon+label row for both states ──
// In collapsed (56px container), padding math centers the icon:
//   outer 8px + button padding 8px + half-icon 12px = 28px = center of 56px strip ✓
// Labels fade with opacity transition so they disappear before the container clips them.

function NavRow({
  icon,
  label,
  onClick,
  isCollapsed,
}: {
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
  isCollapsed: boolean;
}) {
  return (
    <button
      onClick={onClick}
      title={isCollapsed ? label : undefined}
      className="cx-nav-row"
    >
      <span className="cx-nav-row-icon">{icon}</span>
      <span
        className={`cx-nav-row-label${isCollapsed ? " cx-nav-row-label--hidden" : ""}`}
      >
        {label}
      </span>
    </button>
  );
}

// ── ConvItem — conversation list item with inline rename + ··· dropdown ──

function ConvItem({
  conv,
  isActive,
  isLoading,
  isStreaming,
  isPinned,
  onClick,
  onRename,
  onDelete,
  onPin,
}: {
  conv: Conversation;
  isActive: boolean;
  isLoading: boolean;
  isStreaming: boolean;
  isPinned: boolean;
  onClick: () => void;
  onRename: (id: string, title: string) => void;
  onDelete: () => void;
  onPin: () => void;
}) {
  const { t } = useTranslation();
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(conv.title);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });

  const menuBtnRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Guards against double-commit (onBlur fires when input unmounts after Enter/Escape)
  const committedRef = useRef(false);

  function openMenu(e: React.MouseEvent) {
    e.stopPropagation();
    const btn = menuBtnRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const menuHeight = 120; // approximate: 3 items × ~36px + padding
    let top = rect.bottom + 4;
    let left = rect.left;
    // Reposition if menu would overflow viewport
    if (top + menuHeight > window.innerHeight) {
      top = rect.top - menuHeight - 4;
    }
    if (left + 140 > window.innerWidth) {
      left = window.innerWidth - 148;
    }
    setMenuPos({ top, left });
    setMenuOpen(true);
  }

  function startRename() {
    committedRef.current = false;
    setRenameValue(conv.title);
    setMenuOpen(false);
    setIsRenaming(true);
  }

  function commitRename() {
    if (committedRef.current) return;
    committedRef.current = true;
    const trimmed = renameValue.trim();
    setIsRenaming(false);
    if (trimmed && trimmed !== conv.title) {
      onRename(conv.conversation_id, trimmed);
    }
  }

  function cancelRename() {
    committedRef.current = true; // blocks the onBlur commit that fires when input unmounts
    setIsRenaming(false);
  }

  useEffect(() => {
    if (isRenaming && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isRenaming]);

  useEffect(() => {
    if (!menuOpen) return;
    function onOut() { setMenuOpen(false); }
    document.addEventListener("mousedown", onOut);
    return () => document.removeEventListener("mousedown", onOut);
  }, [menuOpen]);

  return (
    <>
      <div className={`cx-conv-item${isActive ? " cx-conv-item--active" : ""}${isLoading ? " opacity-50" : ""}`}>
        {isRenaming ? (
          <input
            ref={inputRef}
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); commitRename(); }
              if (e.key === "Escape") { e.preventDefault(); cancelRename(); }
            }}
            className="cx-conv-rename-input"
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <button className="cx-conv-title-btn" onClick={onClick} disabled={isLoading} tabIndex={0}>
            {isStreaming && <span className="cx-conv-streaming-dot" />}
            {isLoading && !isStreaming && <span className="cx-conv-streaming-dot" />}
            <span className="cx-conv-title">{conv.title}</span>
          </button>
        )}
        <button
          ref={menuBtnRef}
          className="cx-conv-menu-btn"
          onClick={openMenu}
          aria-label={t("nav.moreOptions")}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <DotsThreeIcon />
        </button>
      </div>
      {menuOpen && (
        <div
          className="cx-conv-dropdown"
          style={{ top: menuPos.top, left: menuPos.left }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <button className="cx-conv-dropdown-item" onClick={() => { setMenuOpen(false); onPin(); }}>
            {isPinned ? t("nav.unpin") : t("nav.pin")}
          </button>
          <button className="cx-conv-dropdown-item" onClick={startRename}>
            {t("nav.rename")}
          </button>
          <button
            className="cx-conv-dropdown-item cx-conv-dropdown-item--danger"
            onClick={() => { setMenuOpen(false); onDelete(); }}
          >
            {t("nav.delete")}
          </button>
        </div>
      )}
    </>
  );
}

// ── Conversation Grouping ──

interface ConversationGroups {
  pinned: Conversation[];
  today: Conversation[];
  week: Conversation[];
  older: Conversation[];
}

function groupConversations(conversations: Conversation[], pinnedIds: string[]): ConversationGroups {
  const pinnedSet = new Set(pinnedIds);
  const pinned: Conversation[] = [];
  const today: Conversation[] = [];
  const week: Conversation[] = [];
  const older: Conversation[] = [];

  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const weekAgo = new Date(todayStart);
  weekAgo.setDate(weekAgo.getDate() - 7);

  for (const c of conversations) {
    if (pinnedSet.has(c.conversation_id)) {
      pinned.push(c);
    } else {
      const d = new Date(c.updated_at);
      if (d >= todayStart) today.push(c);
      else if (d >= weekAgo) week.push(c);
      else older.push(c);
    }
  }

  return { pinned, today, week, older };
}

// ── Sidebar ──

export function Sidebar({ isCollapsed, onToggle, onLoadConversation, onDeleteConversation, onOpenSettings }: SidebarProps) {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const { conversations, conversationId, isStreaming, resetChat, updateConversationTitle, pinnedIds, pinConversation, unpinConversation } = useChatStore();
  const { market } = useMarketStore();
  const clearFeatures = useMapStore((s) => s.clearFeatures);
  const [isDark, setIsDark] = useState(() =>
    typeof document !== "undefined" && document.documentElement.getAttribute("data-theme") === "dark"
  );
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Debounce search query by 150ms
  useEffect(() => {
    debounceRef.current = setTimeout(() => setDebouncedQuery(searchQuery), 150);
    return () => clearTimeout(debounceRef.current);
  }, [searchQuery]);

  function toggleTheme() {
    const html = document.documentElement;
    const next = html.getAttribute("data-theme") === "dark" ? "light" : "dark";
    html.setAttribute("data-theme", next);
    localStorage.setItem("ortvis-theme", next);
    setIsDark(next === "dark");
  }

  function handleNewChat() {
    resetChat();
    clearFeatures();
  }

  async function handleClickConversation(id: string) {
    if (id === conversationId || loadingId) return;
    setLoadingId(id);
    closeSearch();
    try {
      await onLoadConversation(id);
    } finally {
      setLoadingId(null);
    }
  }

  function handleDelete(id: string) {
    onDeleteConversation(id);
  }

  const handleRename = useCallback(async (id: string, title: string) => {
    updateConversationTitle(id, title);
    try {
      await renameConversation(id, title);
    } catch {
      // Silent — local store already updated
    }
  }, [updateConversationTitle]);


  const userInitial = useMemo(
    () => (user?.firstName?.[0] ?? user?.email?.[0] ?? "?").toUpperCase(),
    [user],
  );

  const displayName = user?.firstName || user?.email || "";

  const filteredConversations = useMemo(() => {
    if (!debouncedQuery.trim()) return conversations;
    const q = debouncedQuery.toLowerCase().trim();
    return conversations.filter((c) => c.title.toLowerCase().includes(q));
  }, [conversations, debouncedQuery]);

  const pinnedSet = useMemo(() => new Set(pinnedIds), [pinnedIds]);

  const groups = useMemo(
    () => groupConversations(filteredConversations, pinnedIds),
    [filteredConversations, pinnedIds],
  );

  function openSearch() {
    setSearchOpen(true);
    setSearchQuery("");
  }

  function closeSearch() {
    setSearchOpen(false);
    setSearchQuery("");
  }

  // Auto-focus search input when opening
  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchOpen]);

  return (
    <nav
      aria-label={t("nav.conversations")}
      className="cx-sidebar cx-sidebar-bg flex flex-col h-full overflow-hidden flex-shrink-0"
      style={{ width: isCollapsed ? "56px" : "272px" }}
    >
      {/* ── Header ── */}
      <div
        className="cx-sidebar-header flex items-center flex-shrink-0"
        style={{
          padding: isCollapsed ? "8px" : "10px 8px 8px 16px",
          justifyContent: isCollapsed ? "center" : "space-between",
        }}
      >
        {!isCollapsed && (
          <span className="cx-brand-text">Ortvis</span>
        )}
        <button onClick={onToggle} className="cx-sidebar-icon" title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}>
          <PanelIcon />
        </button>
      </div>

      {/* ── Nav rows / Search input ── */}
      <div className="cx-sidebar-nav">
        {searchOpen && !isCollapsed ? (
          <div className="cx-search-row">
            <span className="cx-search-row-icon"><SearchIcon /></span>
            <input
              ref={searchInputRef}
              className="cx-search-input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  e.preventDefault();
                  if (searchQuery) {
                    setSearchQuery("");
                  } else {
                    closeSearch();
                  }
                }
              }}
              placeholder={t("nav.searchPlaceholder")}
            />
          </div>
        ) : (
          <>
            <NavRow
              icon={<PlusIcon />}
              label={t("chat.newConversation")}
              onClick={handleNewChat}
              isCollapsed={isCollapsed}
            />
            <NavRow
              icon={<SearchIcon />}
              label={t("nav.search")}
              onClick={() => {
                if (isCollapsed) {
                  onToggle();
                  // Delay opening search until sidebar expands
                  setTimeout(openSearch, 200);
                } else {
                  openSearch();
                }
              }}
              isCollapsed={isCollapsed}
            />
          </>
        )}
      </div>

      {/* ── Separator ── */}
      <div className="cx-sidebar-separator" />

      {/* ── Conversation area ── */}
      {isCollapsed ? (
        <div className="cx-sidebar-collapsed-area flex flex-col items-center flex-1">
          <button
            className={`cx-sidebar-icon${conversationId ? " cx-sidebar-icon--active" : ""}`}
            title={t("nav.conversations")}
            onClick={onToggle}
          >
            <ChatBubbleIcon />
          </button>
        </div>
      ) : (
        <div className="flex flex-col flex-1 overflow-hidden">
          <div className="flex flex-col flex-1 overflow-y-auto py-1">
            {filteredConversations.length === 0 ? (
              <p className="cx-sidebar-empty">
                {searchOpen && searchQuery.trim() ? t("nav.noResults") : t("chat.noConversations")}
              </p>
            ) : (
              <>
                {(
                  [
                    ["pinned", groups.pinned],
                    ["today", groups.today],
                    ["previousWeek", groups.week],
                    ["older", groups.older],
                  ] as [string, Conversation[]][]
                ).map(
                  ([key, convs]) =>
                    convs.length > 0 && (
                      <div key={key}>
                        <div className="cx-settings-section-label cx-sidebar-section-label">
                          {t(`nav.${key}`)}
                        </div>
                        {convs.map((conv) => (
                          <ConvItem
                            key={conv.conversation_id}
                            conv={conv}
                            isActive={conv.conversation_id === conversationId}
                            isLoading={conv.conversation_id === loadingId}
                            isStreaming={conv.conversation_id === conversationId && isStreaming}
                            isPinned={pinnedSet.has(conv.conversation_id)}
                            onClick={() => handleClickConversation(conv.conversation_id)}
                            onRename={handleRename}
                            onDelete={() => handleDelete(conv.conversation_id)}
                            onPin={() => {
                              if (pinnedSet.has(conv.conversation_id)) {
                                unpinConversation(conv.conversation_id);
                              } else {
                                pinConversation(conv.conversation_id);
                              }
                            }}
                          />
                        ))}
                      </div>
                    ),
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Bottom ── */}
      {isCollapsed ? (
        <div className="cx-sidebar-collapsed-bottom flex flex-col items-center flex-shrink-0">
          <button
            className="cx-sidebar-icon"
            onClick={toggleTheme}
            aria-label={isDark ? t("settings.light") : t("settings.dark")}
            title={isDark ? t("settings.light") : t("settings.dark")}
          >
            {isDark ? <SunIcon /> : <MoonIcon />}
          </button>
          <button
            className="cx-sidebar-icon"
            onClick={onOpenSettings}
            title="Settings"
          >
            <GearIcon />
          </button>
          <div className="cx-avatar cx-avatar--md" title={user?.email}>
            {userInitial}
          </div>
        </div>
      ) : (
        <div className="cx-sidebar-footer">
          {/* Avatar */}
          <div className="cx-avatar cx-avatar--sm" title={user?.email}>
            {userInitial}
          </div>

          {/* Name + market */}
          <div className="cx-sidebar-user-info">
            <div className="cx-sidebar-user-name">{displayName}</div>
            <div className="cx-sidebar-market-label">{t(`market.${market}`)}</div>
          </div>

          {/* Theme toggle */}
          <button
            className="cx-sidebar-icon flex-shrink-0"
            onClick={toggleTheme}
            aria-label={isDark ? t("settings.light") : t("settings.dark")}
            title={isDark ? t("settings.light") : t("settings.dark")}
          >
            {isDark ? <SunIcon /> : <MoonIcon />}
          </button>

          {/* Gear */}
          <button
            className="cx-sidebar-icon cx-sidebar-settings-btn flex-shrink-0"
            onClick={onOpenSettings}
            title="Settings"
          >
            <GearIcon />
          </button>
        </div>
      )}

    </nav>
  );
}
